import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LANGUAGE, LANGUAGE_CODES } from '~shared/i18n/languages';
import { resolveLanguage } from '~shared/i18n/resolve-language';
import ruCommon from '~shared/i18n/locales/ru/common.json';
import ruLanding from '~shared/i18n/locales/ru/landing.json';
import ruPrizesUi from '~shared/i18n/locales/ru/prizes-ui.json';
import ruAuth from '~shared/i18n/locales/ru/auth.json';
import azCommon from '~shared/i18n/locales/az/common.json';
import azLanding from '~shared/i18n/locales/az/landing.json';
import azPrizesUi from '~shared/i18n/locales/az/prizes-ui.json';
import azAuth from '~shared/i18n/locales/az/auth.json';
import beCommon from '~shared/i18n/locales/be/common.json';
import beLanding from '~shared/i18n/locales/be/landing.json';
import bePrizesUi from '~shared/i18n/locales/be/prizes-ui.json';
import beAuth from '~shared/i18n/locales/be/auth.json';
import kaCommon from '~shared/i18n/locales/ka/common.json';
import kaLanding from '~shared/i18n/locales/ka/landing.json';
import kaPrizesUi from '~shared/i18n/locales/ka/prizes-ui.json';
import kaAuth from '~shared/i18n/locales/ka/auth.json';
import kyCommon from '~shared/i18n/locales/ky/common.json';
import kyLanding from '~shared/i18n/locales/ky/landing.json';
import kyPrizesUi from '~shared/i18n/locales/ky/prizes-ui.json';
import kyAuth from '~shared/i18n/locales/ky/auth.json';
import kkCommon from '~shared/i18n/locales/kk/common.json';
import kkLanding from '~shared/i18n/locales/kk/landing.json';
import kkPrizesUi from '~shared/i18n/locales/kk/prizes-ui.json';
import kkAuth from '~shared/i18n/locales/kk/auth.json';
import roCommon from '~shared/i18n/locales/ro/common.json';
import roLanding from '~shared/i18n/locales/ro/landing.json';
import roPrizesUi from '~shared/i18n/locales/ro/prizes-ui.json';
import roAuth from '~shared/i18n/locales/ro/auth.json';
import tgCommon from '~shared/i18n/locales/tg/common.json';
import tgLanding from '~shared/i18n/locales/tg/landing.json';
import tgPrizesUi from '~shared/i18n/locales/tg/prizes-ui.json';
import tgAuth from '~shared/i18n/locales/tg/auth.json';
import uzCommon from '~shared/i18n/locales/uz/common.json';
import uzLanding from '~shared/i18n/locales/uz/landing.json';
import uzPrizesUi from '~shared/i18n/locales/uz/prizes-ui.json';
import uzAuth from '~shared/i18n/locales/uz/auth.json';

/**
 * Переводы интерфейса (localization). Каталоги — `shared/i18n/locales/<lng>/<ns>.json`, импортируются
 * статически и инициализируются синхронно до рендера: каталоги маленькие, ленивая загрузка дала бы мигание русского.
 * Новый язык: каталоги + строка в `shared/i18n/languages.ts` + запись в `resources` ниже.
 */
export const resources = {
  ru: { common: ruCommon, landing: ruLanding, 'prizes-ui': ruPrizesUi, auth: ruAuth },
  az: { common: azCommon, landing: azLanding, 'prizes-ui': azPrizesUi, auth: azAuth },
  be: { common: beCommon, landing: beLanding, 'prizes-ui': bePrizesUi, auth: beAuth },
  ka: { common: kaCommon, landing: kaLanding, 'prizes-ui': kaPrizesUi, auth: kaAuth },
  ky: { common: kyCommon, landing: kyLanding, 'prizes-ui': kyPrizesUi, auth: kyAuth },
  kk: { common: kkCommon, landing: kkLanding, 'prizes-ui': kkPrizesUi, auth: kkAuth },
  ro: { common: roCommon, landing: roLanding, 'prizes-ui': roPrizesUi, auth: roAuth },
  tg: { common: tgCommon, landing: tgLanding, 'prizes-ui': tgPrizesUi, auth: tgAuth },
  uz: { common: uzCommon, landing: uzLanding, 'prizes-ui': uzPrizesUi, auth: uzAuth },
} as const;

/** Явный выбор в переключателе. AICODE-NOTE: пишется только из chooseLanguage(), не при автоопределении — иначе язык браузера «залипнет». */
const STORAGE_KEY = 'ny_lang';

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null; // хранилище запрещено — выбор живёт до перезагрузки
  }
}

i18next.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
  document.title = i18next.t('common:pageTitle');
});

void i18next.use(initReactI18next).init({
  resources,
  lng: resolveLanguage({ stored: readStored(), browserLanguages: navigator.languages ?? [], supported: LANGUAGE_CODES }),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: LANGUAGE_CODES,
  defaultNS: 'common',
  ns: ['common', 'landing', 'prizes-ui', 'auth'],
  initAsync: false,
  interpolation: { escapeValue: false }, // React экранирует сам
});

/** Явный выбор языка участником: переключить и запомнить в браузере. */
export function chooseLanguage(code: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // хранилище запрещено — язык всё равно переключится
  }
  void i18next.changeLanguage(code);
}

export { i18next };
